import DomainDetailsPanel from "./DomainDetailsPanel";
import { DomainStats, DomainTableRow } from "utils/analytics/types";
import { useEffect, useMemo } from "react";
import { format, parseISO } from "date-fns";
import { useAnalyticsFilters } from "providers/AnalyticsProvider/AnalyticsFilterProvider";
import {
  PRODUCT_ABBREVIATIONS,
  PRODUCT_FULL_NAMES,
  PRODUCT_BADGE_COLORS,
  CHART_COLORS,
} from "utils/analytics/constants";
import { getCode } from "country-list";
import { useDomainStats } from "hooks/analytics";
import { Line } from "react-chartjs-2";
import type { ChartOptions, ScriptableContext, TooltipItem } from "chart.js";
import { PureTooltip } from "components/Tooltip";
import { fmtNumber, fmtShortNumber } from "utils";
import {
  BASE_LINE_CHART_OPTIONS,
  CHART_TICK_COLOR,
  CHART_GRID_COLOR,
  createLineDataset,
} from "../charts/lineChart";


const abbreviateLocation = (location: string): string => {
  if (!location) return "";
  if (location.length <= 3) return location.toUpperCase();

  // Try to get ISO country code from country-list
  const code = getCode(location);
  return code || location.slice(0, 2).toUpperCase();
};

const abbreviateProduct = (product: string): string => {
  if (!product) return "";
  return PRODUCT_ABBREVIATIONS[product] || product.slice(0, 1).toUpperCase();
};

// combine aiparser and aiparserAdmin together
const consolidateProductSources = (products: string[]): string[] =>
  Array.from(
    new Set(products.map((p) => (p === "aiParserAdmin" ? "aiparser" : p))),
  );

const getFullProductName = (product: string): string => {
  const abbreviation = abbreviateProduct(product);
  return PRODUCT_FULL_NAMES[abbreviation] || product;
};

// Same format as the overview stat card (AnalyticsQuickStats); keep the two identical.
const formatResponseTime = (ms: number): string =>
  ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`;

// Day buckets arrive as "yyyy-MM-dd". new Date() would read that as UTC midnight
// and shift it into the previous day for viewers west of UTC; parseISO keeps it a calendar day.
const formatDateForDisplay = (date: string): string => format(parseISO(date), "MMM d");



const ProductBadge = ({
  product,
  index,
}: {
  product: string;
  index: number;
}) => {
  const colorClass = PRODUCT_BADGE_COLORS[index % PRODUCT_BADGE_COLORS.length];
  const abbreviation = abbreviateProduct(product)?.toUpperCase();
  const fullName = getFullProductName(product);

  return (
    <PureTooltip content={fullName} className="inline-block">
      <span
        className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}
      >
        {abbreviation}
      </span>
    </PureTooltip>
  );
};

const LocationBadge = ({ location }: { location: string }) => (
  <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
    {abbreviateLocation(location)?.toUpperCase()}
  </span>
);

// Chart config.
const createChartOptions = (
  dataLength: number,
  cumulativeData: DomainStats["cumulativeData"]
) => {
  // Calculate the maximum value
  const maxValue = Math.max(
    ...cumulativeData.flatMap((d) => [
      d.cumulative_failure || 0,
      d.cumulative_success || 0,
    ])
  );

  // Calculate a suggested max
  let suggestedMax;
  if (maxValue === 0) {
    suggestedMax = 10;
  } else if (maxValue < 5) {
    suggestedMax = 5;
  } else if (maxValue < 10) {
    suggestedMax = 10;
  } else if (maxValue < 50) {
    suggestedMax = Math.ceil(maxValue / 10) * 10;
  } else if (maxValue < 100) {
    suggestedMax = Math.ceil(maxValue / 20) * 20;
  } else {
    suggestedMax = Math.ceil(maxValue * 1.1 / 50) * 50;
  }

  const pointColor = (context: ScriptableContext<"line">) =>
    context.datasetIndex === 0 ? CHART_COLORS.FAILURE : CHART_COLORS.SUCCESS;

  const options: ChartOptions<"line"> = {
    ...BASE_LINE_CHART_OPTIONS,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
        displayColors: true,
        callbacks: {
          label: (context: TooltipItem<"line">) => {
            let label = context.dataset.label || "";
            if (label) label += ": ";
            if (context.parsed.y !== null) {
              label += fmtNumber(context.parsed.y);
            }
            return label;
          },
        },
      },
    },
    elements: {
      point: {
        radius: dataLength <= 2 ? 5 : 3,
        hoverRadius: dataLength <= 2 ? 7 : 5,
        backgroundColor: pointColor,
        borderColor: pointColor,
        borderWidth: 2,
      },
    },
    scales: {
      x: {
        type: dataLength === 1 ? "category" : "time",
        ...(dataLength > 1
          ? {
              time: {
                unit: "day" as const,
                displayFormats: {
                  day: "MMM d",
                },
                tooltipFormat: "PP",
              },
            }
          : {}),
        ticks: {
          color: CHART_TICK_COLOR,
          font: {
            size: 10,
          },
          autoSkip: false,
          maxTicksLimit: dataLength <= 2 ? 2 : 5,
        },
        grid: {
          display: false,
        },
      },
      y: {
        position: "left",
        ticks: {
          color: CHART_TICK_COLOR,
          font: {
            size: 10,
          },
          stepSize: 1,
          precision: 0,
          callback: (value) => {
            // Only show integer values
            if (typeof value === "number" && Math.floor(value) === value) {
              return fmtShortNumber(value);
            }
            return null; // Hide non-integer ticks
          },
        },
        grid: {
          color: CHART_GRID_COLOR,
        },
        border: {
          display: false,
        },
        beginAtZero: true,
        min: 0,
        suggestedMax: suggestedMax,
      },
    },
  };

  return options;
};

const DetailsChart = ({ details }: { details: DomainStats }) => {
  const dataLength = details.cumulativeData.length;

  const chartData = useMemo(
    () => ({
      labels:
        dataLength === 1
          ? ["", formatDateForDisplay(details.cumulativeData[0].date), ""]
          : undefined,
      datasets: [
        createLineDataset({
          label: "Failed requests",
          color: CHART_COLORS.FAILURE,
          data: details.cumulativeData.map((d) => ({
            x:
              dataLength === 1
                ? formatDateForDisplay(d.date)
                : d.date,
            y: d.cumulative_failure,
          })),
          tension: dataLength <= 2 ? 0 : 0.3,
          pointRadius: dataLength <= 2 ? 5 : 3,
          pointHoverRadius: dataLength <= 2 ? 7 : 5,
          borderWidth: 2,
        }),
        createLineDataset({
          label: "Successful requests",
          color: CHART_COLORS.SUCCESS,
          data: details.cumulativeData.map((d) => ({
            x:
              dataLength === 1
                ? formatDateForDisplay(d.date)
                : d.date,
            y: d.cumulative_success,
          })),
          tension: dataLength <= 2 ? 0 : 0.3,
          pointRadius: dataLength <= 2 ? 5 : 3,
          pointHoverRadius: dataLength <= 2 ? 7 : 5,
          borderWidth: 2,
        }),
      ],
    }),
    [details.cumulativeData, dataLength]
  );

  const options = useMemo(
    () => createChartOptions(dataLength, details.cumulativeData),
    [details.cumulativeData, dataLength]
  );

  return (
    <div className="h-64 min-h-[16rem]">
      <div className="w-full h-full relative">
        <Line options={options} data={chartData} />
      </div>
    </div>
  );
};

const DomainDetails = ({ details }: { details: DomainStats }) => (
  <div className="px-6 py-6">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-8">
          <div className="space-y-3">
            <MetricRow
              label="Avg. concurrency"
              value={Math.round(details.avgConcurrency)}
            />
            <MetricRow
              label="Avg. response time"
              value={formatResponseTime(details.avgResponseTime)}
            />

            <div className="flex items-center">
              <span className="text-gray-500 text-sm">Products:</span>
              <div className="flex gap-1 flex-wrap ml-2">
                {details.products.length > 0 ? (
                  consolidateProductSources(details.products).map((p, idx) => (
                    <ProductBadge key={p} product={p} index={idx} />
                  ))
                ) : (
                  <span className="text-gray-400 text-sm">N/A</span>
                )}
              </div>
            </div>

            <div className="flex items-center">
              <span className="text-gray-500 text-sm">Locations:</span>
              <div className="flex gap-1 flex-wrap ml-2">
                {details.countries.length > 0 ? (
                  details.countries.map((c: string) => (
                    <LocationBadge key={c} location={c} />
                  ))
                ) : (
                  <span className="text-gray-400 text-sm">N/A</span>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center">
              <div
                className="w-3 h-3 rounded-sm mr-2"
                style={{ backgroundColor: CHART_COLORS.SUCCESS }}
              />
              <span className="text-gray-500 text-sm">
                Successful requests:
              </span>
              <span className="font-semibold text-gray-900 ml-2">
                {fmtNumber(details.totalSuccess)}
              </span>
            </div>

            <div className="flex items-center">
              <div
                className="w-3 h-3 rounded-sm mr-2"
                style={{ backgroundColor: CHART_COLORS.FAILURE }}
              />
              <span className="text-gray-500 text-sm">Failed requests:</span>
              <span className="font-semibold text-gray-900 ml-2">
                {fmtNumber(details.totalFailure)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <DetailsChart details={details} />
    </div>
  </div>
);

// Metric Row Component
const MetricRow = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <div className="flex items-center">
    <span className="text-gray-500 text-sm">{label}:</span>
    <span className="font-semibold text-gray-900 ml-2">{value}</span>
  </div>
);

/** Mounted only while its row is expanded, so the details fetch happens on mount. */
export const DomainExpandedRow = ({ item }: { item: DomainTableRow }) => {
  const { dateRange, filters } = useAnalyticsFilters();

  const { stats, loading, error, fetchStats } = useDomainStats({
    domain: item.domain,
    dateRange,
    filters,
    itemLocation: item.location,
  });

  // Fetch once per open: the row remounts on each expand, and fetchStats is a new function every render
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-[#ecf0f3] border-t">
      <DomainDetailsPanel
        loading={loading}
        stats={stats}
        error={!!error}
        DomainDetails={DomainDetails}
      />
    </div>
  );
};

